import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Chip,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import ReactGA from 'react-ga4';
import { ExperienceType, FeeType } from '../../enums/experience';

export type TypeFilter = FeeType | 'All';
export type FeeSortKey = 'nameAsc' | 'nameDesc' | 'createdDesc' | 'createdAsc';

const ALL_EXPERIENCES: ExperienceType[] = Object.values(
    ExperienceType,
) as ExperienceType[];

export default function FeesToolbar({
    query,
    onQueryChange,
    typeFilter,
    onTypeFilterChange,
    expFilter,
    onExpFilterChange,
    sortKey,
    onSortKeyChange,
}: {
    query: string;
    onQueryChange: (v: string) => void;
    typeFilter: TypeFilter;
    onTypeFilterChange: (v: TypeFilter) => void;
    expFilter: ExperienceType[];
    onExpFilterChange: (v: ExperienceType[]) => void;
    sortKey: FeeSortKey;
    onSortKeyChange: (v: FeeSortKey) => void;
}) {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const handleAddClick = () => {
        navigate(`/newFee`);
    };

    return (
        <Stack
            sx={{
                py: isMobile || isIpad ? 1 : 2,
                width: '100%',
            }}
        >
            <Grid container spacing={1.5} alignItems='center'>
                {/* Left: helper text */}
                <Grid item xs={12} md>
                    <Typography sx={{ fontSize: '12px' }}>
                        Create custom fee types and apply them to your
                        experiences so users see costs upfront.
                    </Typography>
                </Grid>

                {/* Right: Add */}
                <Grid item xs={12} md='auto' sx={{ textAlign: 'right' }}>
                    {isMobile || isIpad ? (
                        <CustomIconButton
                            variant='contained'
                            color='primary'
                            onClick={handleAddClick}
                        >
                            <AddCircleIcon />
                        </CustomIconButton>
                    ) : (
                        <CustomButton
                            id='addFeeType'
                            variant='contained'
                            color='primary'
                            startIcon={<AddCircleIcon />}
                            onClick={handleAddClick}
                        >
                            Add New
                        </CustomButton>
                    )}
                </Grid>
            </Grid>

            {/* Controls row */}
            <Grid container spacing={1.5} sx={{ paddingTop: '30px' }}>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        size='small'
                        placeholder='Search fees…'
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={2.5}>
                    <Select
                        fullWidth
                        size='small'
                        value={typeFilter}
                        onChange={(e) =>
                            onTypeFilterChange(e.target.value as TypeFilter)
                        }
                    >
                        <MenuItem value='All'>Type: All</MenuItem>
                        <MenuItem value='fixed'>Fixed</MenuItem>
                        <MenuItem value='percent'>Percent</MenuItem>
                        <MenuItem value='perHour'>Per Hour</MenuItem>
                    </Select>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Autocomplete
                        multiple
                        options={ALL_EXPERIENCES}
                        size='small'
                        value={expFilter}
                        onChange={(_, v) => onExpFilterChange(v)}
                        // Capitalize the options shown in the dropdown
                        getOptionLabel={(option) =>
                            option.charAt(0).toUpperCase() + option.slice(1)
                        }
                        renderInput={(params) => (
                            <TextField
                                sx={{
                                    '& .MuiInputBase-root': { borderRadius: 2 },
                                }}
                                {...params}
                                label='Experience types'
                                placeholder='All'
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    size='small'
                                    // Capitalize the chip labels too
                                    label={
                                        option.charAt(0).toUpperCase() +
                                        option.slice(1)
                                    }
                                />
                            ))
                        }
                    />
                </Grid>

                <Grid item xs={12} md={1.5}>
                    <Select
                        fullWidth
                        size='small'
                        value={sortKey}
                        onChange={(e) => onSortKeyChange(e.target.value as any)}
                    >
                        <MenuItem value='nameAsc'>Sort: Name ↑</MenuItem>
                        <MenuItem value='nameDesc'>Sort: Name ↓</MenuItem>
                        <MenuItem value='createdDesc'>Sort: Newest</MenuItem>
                        <MenuItem value='createdAsc'>Sort: Oldest</MenuItem>
                    </Select>
                </Grid>
            </Grid>
        </Stack>
    );
}
